<template>
  <div class="container mt-5">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4" style="color: #b03a45;">
        <i class="fas fa-file-alt me-2 text-warning"></i>{{ t('editDocuments.title') }}
      </h4>

      <!-- Upload form -->
      <form @submit.prevent="handleUpload">
        <div class="row">
          <!-- Type de document -->
          <div class="col-md-6 mb-4">
            <label class="form-label fw-bold" style="color: #b03a45;">{{ t('editDocuments.fields.documentType') }}</label>
            <select v-model="form.document_type_id" class="form-select" required>
              <option disabled value="">{{ t('editDocuments.placeholders.selectType') }}</option>
              <option v-for="type in documentTypes" :key="type.id" :value="type.id">
                {{ type.name }}
              </option>
            </select>
          </div>

          <!-- Title -->
          <div class="col-md-6 mb-4">
            <label class="form-label fw-bold" style="color: #b03a45;">{{ t('editDocuments.fields.title') }}</label>
            <input
              type="text"
              v-model="form.title"
              class="form-control border rounded px-3"
              :placeholder="t('editDocuments.placeholders.title')"
            />
          </div>

          <!-- File Upload -->
          <div class="col-md-12 mb-4">
            <label class="form-label fw-bold" style="color: #b03a45;">{{ t('editDocuments.fields.file') }}</label>
            <input type="file" @change="handleFileChange" class="form-control" required />
          </div>

          <!-- Buttons -->
          <div class="d-flex justify-content-end gap-2">
            <button type="submit" class="btn text-white" style="background-color: #b03a45;">
              {{ t('editDocuments.buttons.upload') }}
            </button>
            <button type="button" class="btn btn-secondary" @click="goBackToList">
              {{ t('editDocuments.buttons.finish') }}
            </button>
          </div>
        </div>
      </form>

      <hr class="my-4" />

      <!-- Uploaded docs -->
      <h5 class="mb-3 fw-bold" style="color: #b03a45;">
        <i class="fas fa-folder-open me-2"></i> {{ t('editDocuments.sections.uploaded') }}
      </h5>

      <ul v-if="uploadedDocuments.length" class="list-group">
        <li
          v-for="doc in uploadedDocuments"
          :key="doc.id"
          class="list-group-item d-flex justify-content-between align-items-center"
        >
          <div class="d-flex align-items-center gap-2" style="flex: 1;">
            <i class="fas fa-file-alt text-secondary"></i>

            <!-- Input Title (modification directe) -->
            <input
              v-model="doc.title"
              type="text"
              class="form-control form-control-sm"
              style="max-width: 250px;"
              :placeholder="t('editDocuments.placeholders.title')"
              @blur="updateTitle(doc)"
            />
          </div>

          <div class="btn-group">
            <a
              :href="getDownloadUrl(doc.file_path)"
              target="_blank"
              download
              class="btn btn-sm btn-outline-primary"
            >
              {{ t('editDocuments.buttons.export') }}
            </a>
            <button @click="handleDelete(doc.id)" class="btn btn-sm btn-outline-danger">
              {{ t('editDocuments.buttons.delete') }}
            </button>
          </div>
        </li>
      </ul>

      <p v-else class="text-muted">{{ t('editDocuments.messages.noDocuments') }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import axios from '@/api'

const { t } = useI18n() // ✅ active la traduction
const route = useRoute()
const router = useRouter()
const toast = useToast()

const userId = route.query.user_id
const documentTypes = ref([])
const uploadedDocuments = ref([])

const form = ref({
  user_id: userId,
  document_type_id: '',
  title: '',
  file: null
})

const fetchDocumentTypes = async () => {
  try {
    // Temporaire: utiliser des données statiques car la route n'existe pas encore
    const staticTypes = [
      { id: 1, name: 'CV' },
      { id: 2, name: 'Lettre de motivation' },
      { id: 3, name: 'Contrat de travail' },
      { id: 4, name: 'Bulletin de paie' },
      { id: 5, name: 'Carte d\'identité' },
      { id: 6, name: 'Passeport' },
      { id: 7, name: 'Permis de conduire' },
      { id: 8, name: 'Diplôme' },
      { id: 9, name: 'Certificat de travail' },
      { id: 10, name: 'RIB' },
      { id: 11, name: 'Autre' }
    ]
    documentTypes.value = staticTypes
  } catch {
    toast.error(t('editDocuments.messages.loadTypesFailed'))
  }
}

const fetchUploadedDocuments = async () => {
  try {
    const { data } = await axios.get(`/employee/documents/${userId}`)
    if (data.message && data.message.includes('No documents found')) {
      uploadedDocuments.value = []
      toast.info(data.message)
    } else {
      uploadedDocuments.value = data.data || []
    }
  } catch (error) {
    if (error.response?.status === 404) {
      uploadedDocuments.value = []
      toast.info('Cet utilisateur n\'a pas de documents')
    } else {
      toast.error(t('editDocuments.messages.loadDocsFailed'))
    }
  }
}

const handleFileChange = (e) => {
  const file = e.target.files?.[0]
  if (file) form.value.file = file
}

const handleUpload = async () => {
  try {
    const payload = new FormData()
    for (const key in form.value) {
      if (form.value[key]) payload.append(key, form.value[key])
    }

    await axios.post('/employee/documents', payload, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    toast.success(t('editDocuments.messages.uploadSuccess'))
    await fetchUploadedDocuments()

    form.value.document_type_id = ''
    form.value.title = ''
    form.value.file = null
  } catch {
    toast.error(t('editDocuments.messages.uploadFailed'))
  }
}

const handleDelete = async (id) => {
  try {
    await axios.delete(`/employee/documents/${id}`)
    toast.success(t('editDocuments.messages.deleteSuccess'))
    await fetchUploadedDocuments()
  } catch {
    toast.error(t('editDocuments.messages.deleteFailed'))
  }
}

const updateTitle = async (doc) => {
  try {
    await axios.put(`/employee/documents/${doc.id}`, { title: doc.title })
    toast.success(t('editDocuments.messages.updateTitleSuccess'))
    await fetchUploadedDocuments()
  } catch {
    toast.error(t('editDocuments.messages.updateTitleFailed'))
  }
}

const getDownloadUrl = (filePath) => `http://127.0.0.1:8000/storage/${filePath}`

const goBackToList = () => router.push('/employees')

onMounted(() => {
  fetchDocumentTypes()
  fetchUploadedDocuments()
})
</script>

<style scoped>
.list-group-item {
  border-radius: 10px;
  margin-bottom: 0.5rem;
  border: 1px solid #dee2e6;
}
</style>
